import React, {
    useState,
    useEffect,
    useRef,
    useCallback,
    useLayoutEffect,
} from 'react'
import { Okpd2 } from '../../../../../Org/Handbook/types'
import { OrganizationInfo } from '../../../../../Org/Home/types'

import { Purchase, Country } from '../../../../types'
import { CurrencyList } from '../../../types'

import HoverTooltip from '../../../../../../../components/HoverTooltip'

type PerfTotalDocSum = {
    total_doc: number
    total_doc_rub: number
}

type PerfTotalPaySum = {
    total_payment: number
    total_payment_rub: number
}

type Props = {
    purchaseGuid: string
    perf_number: number
    purchase: Purchase
    theme: string
    isActive: boolean
    currencyList: CurrencyList[]
    selected: boolean
    onDoubleClick: (contract_guid: string) => void
}

const CONTRACT_STATUS = new Map([
    ['F', 'Редактирование'],
    ['P', 'Размещено'],
    ['I', 'Недействительно'],
    ['M', 'Изменение'],
])

const getCurrentDate = (): string => {
    const today = new Date()
    const year = today.getFullYear()
    const month = String(today.getMonth() + 1).padStart(2, '0') // добавляем 1, так как месяцы начинаются с 0
    const day = String(today.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
}

function formatCountryOfOriginStrict(src: Country | null | undefined): string {
    const list = src?.country ?? []
    if (!Array.isArray(list) || list.length === 0) return ''

    const names = list.map((c) => (c?.name ?? '').trim()).filter(Boolean)

    // убрать дубли, сохраняя порядок
    const seen = new Set<string>()
    const norm = (s: string) => s.toLocaleLowerCase('ru-RU')
    const unique = names.filter((n) => {
        const k = norm(n)
        if (seen.has(k)) return false
        seen.add(k)
        return true
    })

    // РФ — первой
    const rfIndex = unique.findIndex((n) => norm(n) === 'российская федерация')
    if (rfIndex > 0) {
        const [rf] = unique.splice(rfIndex, 1)
        unique.unshift(rf)
    }

    return unique.join(', ')
}

function getCountryListOrderedStrict(
    src: Country | null | undefined
): string[] {
    const list = src?.country ?? []
    if (!Array.isArray(list) || list.length === 0) return []

    const names = list.map((c) => (c?.name ?? '').trim()).filter(Boolean)

    // dedupe (без регистра), сохранив порядок
    const seen = new Set<string>()
    const norm = (s: string) => s.toLocaleLowerCase('ru-RU')
    const unique = names.filter((n) => {
        const k = norm(n)
        if (seen.has(k)) return false
        seen.add(k)
        return true
    })

    // РФ — первой
    const rfIndex = unique.findIndex((n) => norm(n) === 'российская федерация')
    if (rfIndex > 0) {
        const [rf] = unique.splice(rfIndex, 1)
        unique.unshift(rf)
    }

    return unique
}

function purchaseTypeLabel(code?: string | null): string {
    switch ((code ?? '').toUpperCase()) {
        case 'G':
            return 'Товар'
        case 'W':
            return 'Работа'
        case 'S':
            return 'Услуга'
        default:
            return '' // неизвестно/пусто
    }
}

function formatTotalCount(totalCount: number) {
    // Получаем число с 5 знаками после запятой (с округлением)
    let str = totalCount.toFixed(5)
    // Если есть десятичная часть, удаляем лишние нули
    if (str.indexOf('.') !== -1) {
        // Удаляем концевые нули
        str = str.replace(/0+$/, '')
        // Если после удаления нулей осталась точка, убираем её
        str = str.replace(/\.$/, '')
    }
    // Заменяем точку на запятую
    return str.replace('.', ',')
}

const formatNumber = (num: number | string | null | undefined): string => {
    if (num === null || num === undefined) return '-' // Обрабатываем пустые значения

    const parsedNum = typeof num === 'number' ? num : parseFloat(num)

    if (isNaN(parsedNum)) {
        return '-' // Обрабатываем некорректные значения
    }

    return parsedNum
        .toFixed(2) // Два знака после запятой
        .replace(/\B(?=(\d{3})+(?!\d))/g, ' ') // Разделяем тысячные пробелами
        .replace('.', ',') // Заменяем точку на запятую
}

const PurchaseTableRow: React.FC<Props> = (props) => {
    const STATUS_COLOR =
        props.theme == 'dark'
            ? new Map([
                  ['Черновик', '#9951FE'],
                  ['Готово', '#049664'],
                  //   ['Подготовка', '#745CFF'],
                  //   ['Готово к отправке', '#563AF5'],
                  ['Готово к отправке', '#9229EE'],
                  ['Отправлено', '#049664'],
                  ['Идет прием заявок', '#049664'],
                  ['Подача заявок', '#049664'],
                  ['Работа комиссии', '#6E0AC7'],
                  ['Закупка состоялась', '#63099F'],
                  ['Закупка не состоялась', '#515055'],
                  ['Закупка отменена', '#515055'],
                  ['Закупка завершена', '#515055'],
                  ['Недействительно', '#636366'],
              ])
            : new Map([
                  ['Черновик', '#EADFFF'],
                  ['Готово', '#83D2B7'],
                  //   ['Подготовка', '#745CFF'],
                  //   ['Готово к отправке', '#563AF5'],
                  ['Готово к отправке', '#E3CFFF'],
                  ['Отправлено', '#83D2B7'],
                  ['Идет прием заявок', '#83D2B7'],
                  ['Подача заявок', '#83D2B7'],
                  ['Работа комиссии', '#DAAFFD'],
                  ['Закупка состоялась', '#C483F5'],
                  ['Закупка не состоялась', '#CFD6E2'],
                  ['Закупка отменена', '#CFD6E2'],
                  ['Закупка завершена', '#CFD6E2'],
                  ['Недействительно', '#CFD6E2'],
              ])

    const STAGE_COLOR =
        props.theme == 'dark'
            ? new Map([
                  ['Редактирование', '#9229EE'],
                  //   ['Подготовка к размещению', '#563AF5'],
                  ['Отправка', '#6E0AC7'],
                  ['Подача заявок', '#049664'],
                  ['Работа комиссии', '#63099F'],
                  ['Закупка завершена', '#42036C'],
                  ['Закупка отменена', '#515055'],
              ])
            : new Map([
                  ['Редактирование', '#E3CFFF'],
                  //   ['Подготовка к размещению', '#B7ACFF'],
                  ['Отправка', '#D49FFD'],
                  ['Подача заявок', '#83D2B7'],
                  ['Работа комиссии', '#C483F5'],
                  ['Закупка завершена', '#B759FF'],
                  ['Закупка отменена', '#CFD6E2'],
              ])

    const PURCHASE_STATUS_COLOR =
        props.theme == 'dark'
            ? new Map([
                  ['F', '#8E8E93'],
                  ['P', '#2A282B00'],
                  ['I', '#3A3A3C'],
                  ['M', '#636366'],
              ])
            : new Map([
                  ['F', '#E4E8F0'],
                  ['P', '#F6FBFF00'],
                  ['I', '#B7BECA'],
                  ['M', '#CFD6E2'],
              ])

    const updatedDate = props.purchase.publication_date_time
        ? new Date(props.purchase.publication_date_time)
        : new Date()
    updatedDate.setHours(updatedDate.getHours())

    const formattedUpdateDate = updatedDate
        .toLocaleDateString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        })
        .replace(/\//g, '.')
    const formattedUpdateTime = updatedDate.toLocaleTimeString('ru-RU', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    })

    return (
        <div
            className={
                props.selected
                    ? 'plan-position-table-row-selected'
                    : 'plan-position-table-row'
            }
            onDoubleClick={() => props.onDoubleClick(props.purchase.guid)}
        >
            <div className="flex h-[70px] w-8 items-center justify-center text-center">
                {props.purchase.purchase_stage == 'Редактирование' ? (
                    <input
                        type="checkbox"
                        checked={props.selected}
                        onClick={(e) => e.stopPropagation()}
                    />
                ) : (
                    ''
                )}
            </div>
            <div className="flex flex-row h-[70px] w-8 items-center justify-center text-center ">
                {props.perf_number}
            </div>

            <div
                className={`flex h-[70px] w-24 p-1 items-center justify-center text-center rounded`}
                style={{
                    backgroundColor: props.purchase.purchase_stage
                        ? STAGE_COLOR.get(props.purchase.purchase_stage)
                        : 'transparent',
                }}
            >
                {props.purchase.purchase_stage}
            </div>
            <div
                className="flex h-[70px] w-24 p-1 items-center justify-center text-center rounded"
                style={{
                    backgroundColor: props.purchase.purchase_status
                        ? STATUS_COLOR.get(props.purchase.purchase_status)
                        : 'transparent',
                }}
            >
                {props.purchase.purchase_status == 'Размещена'
                    ? 'Размещено'
                    : props.purchase.purchase_status}
            </div>

            <div className="flex flex-1 h-[70px] text-left items-center justify-center">
                {`Извещение о закупке № ${props.purchase.registration_number ? props.purchase.registration_number : '-'}`}
            </div>

            <div
                className="flex h-[70px] w-24 items-center justify-center text-center rounded"
                style={{
                    backgroundColor: props.purchase.status
                        ? PURCHASE_STATUS_COLOR.get(props.purchase.status)
                        : 'transparent',
                }}
            >
                {CONTRACT_STATUS.get(String(props.purchase.status))}
            </div>

            <div className="flex flex-row h-[70px] w-20 items-center justify-center text-center">
                {props.purchase.version}
            </div>

            <div className="flex h-[70px] w-20 items-center justify-center text-center text-[#6A6A6A] dark:text-[#8A8A8A]">
                {props.purchase.publication_date_time ? (
                    // <>
                    //     {formattedUpdateDate} <br /> {formattedUpdateTime}{' '}
                    // </>
                    <>{formattedUpdateDate}</>
                ) : (
                    '-'
                )}
            </div>
        </div>
    )
}

export default PurchaseTableRow
